import React, { useEffect, useMemo, useState } from "react";
import api from "../api";
import {
  HiOutlineChatBubbleLeftRight,
  HiOutlinePaperAirplane,
  HiOutlineArrowPath,
  HiOutlineCheckCircle,
  HiOutlineExclamationTriangle,
  HiOutlineClock,
  HiOutlineMagnifyingGlass
} from "react-icons/hi2";

const theme = {
  primary: "#0F766E",
  primaryDark: "#115E59",
  primaryLight: "#14B8A6",
  accent: "#2DD4BF",
  background: "#F8FAFC",
  surface: "#FFFFFF",
  surfaceSoft: "#ECFEFF",
  text: "#0F172A",
  textMuted: "#64748B",
  border: "#E2E8F0",
  success: "#16A34A",
  warning: "#F59E0B",
  danger: "#DC2626"
};

function formatDisplayDate(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  });
}

function ManagerRequests({ user }) {
  const [requestType, setRequestType] = useState("target_update");

  const [practice, setPractice] = useState("");
  const [task, setTask] = useState("");
  const [targetValue, setTargetValue] = useState("");

  const [newPracticeName, setNewPracticeName] = useState("");
  const [tasksText, setTasksText] = useState("");

  const [message, setMessage] = useState("");

  const [practices, setPractices] = useState([]);
  const [tasks, setTasks] = useState([]);

  const [history, setHistory] = useState([]);
  const [search, setSearch] = useState("");
  const [submitMessage, setSubmitMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [loadingMasters, setLoadingMasters] = useState(true);

  useEffect(() => {
    if (user?.id) {
      loadHistory();
      loadMasters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadMasters = async () => {
    try {
      setLoadingMasters(true);

      const [practiceRes, taskRes] = await Promise.all([
        api.get("/practices"),
        api.get("/tasks")
      ]);

      setPractices(Array.isArray(practiceRes.data) ? practiceRes.data : []);
      setTasks(Array.isArray(taskRes.data) ? taskRes.data : []);
    } catch (err) {
      console.error("Manager request masters load failed:", err);
      setPractices([]);
      setTasks([]);
    } finally {
      setLoadingMasters(false);
    }
  };

  const loadHistory = async () => {
    try {
      setLoadingHistory(true);
      const res = await api.get(`/manager-requests/manager/${user.id}`);
      setHistory(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Manager requests load failed:", err);
      setHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const filteredTasks = useMemo(() => {
    if (!practice) return [];

    return tasks.filter(
      (item) =>
        String(item.practice || "").trim().toLowerCase() ===
        String(practice || "").trim().toLowerCase()
    );
  }, [tasks, practice]);

  const resetForm = () => {
    setRequestType("target_update");
    setPractice("");
    setTask("");
    setTargetValue("");
    setNewPracticeName("");
    setTasksText("");
    setMessage("");
  };

  const submitRequest = async () => {
    setSubmitMessage("");

    if (requestType === "target_update") {
      if (!String(practice || "").trim()) {
        setSubmitMessage("⚠ Practice is required");
        return;
      }

      if (!String(task || "").trim()) {
        setSubmitMessage("⚠ Task is required");
        return;
      }

      if (!String(targetValue || "").trim()) {
        setSubmitMessage("⚠ Target value is required");
        return;
      }
    }

    if (requestType === "task_addition") {
      if (!String(practice || "").trim()) {
        setSubmitMessage("⚠ Practice is required");
        return;
      }

      if (!String(task || "").trim()) {
        setSubmitMessage("⚠ Task is required");
        return;
      }
    }

    if (requestType === "practice_with_tasks") {
      if (!String(newPracticeName || "").trim()) {
        setSubmitMessage("⚠ New practice name is required");
        return;
      }

      if (!String(tasksText || "").trim()) {
        setSubmitMessage("⚠ Tasks are required");
        return;
      }
    }

    if (!String(message || "").trim()) {
      setSubmitMessage("⚠ Message is required");
      return;
    }

    try {
      setSubmitting(true);

      await api.post("/manager-requests", {
        manager_id: user.id,
        request_type: requestType,
        practice:
          requestType === "practice_with_tasks"
            ? newPracticeName.trim()
            : practice.trim(),
        task:
          requestType === "practice_with_tasks"
            ? tasksText.trim()
            : task.trim(),
        requested_target:
          requestType === "target_update" ? Number(targetValue) : null,
        message: message.trim()
      });

      setSubmitMessage("✅ Request sent to admin successfully");
      resetForm();
      await loadHistory();
    } catch (err) {
      console.error(err);
      setSubmitMessage(
        err.response?.data?.error || "❌ Failed to send request"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const filteredHistory = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return history;

    return history.filter((row) =>
      [
        row.request_type,
        row.practice,
        row.task,
        row.message,
        row.status,
        row.admin_reply
      ]
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [history, search]);

  const getStatusUI = (status) => {
    const s = String(status || "pending").toLowerCase();

    if (s === "approved") {
      return {
        text: "Approved",
        color: theme.success,
        bg: "#DCFCE7"
      };
    }

    if (s === "rejected") {
      return {
        text: "Rejected",
        color: theme.danger,
        bg: "#FEE2E2"
      };
    }

    return {
      text: "Pending",
      color: theme.warning,
      bg: "#FEF3C7"
    };
  };

  const requestTypeLabel = (type) => {
    if (type === "target_update") return "Target Update";
    if (type === "task_addition") return "Add Task To Practice";
    if (type === "practice_with_tasks") return "Add Practice With Tasks";
    return "Request";
  };

  return (
    <div style={page}>
      <div style={hero}>
        <div>
          <h2 style={title}>Manager Requests</h2>
          <p style={subtitle}>
            Send task and target related requests to admin and track replies here.
          </p>
        </div>

        <button
          onClick={() => {
            loadHistory();
            loadMasters();
          }}
          style={refreshBtn}
          type="button"
        >
          <HiOutlineArrowPath style={{ fontSize: 16 }} />
          Refresh
        </button>
      </div>

      <div style={layout}>
        <div style={formCard}>
          <div style={sectionTitleRow}>
            <div style={iconWrap}>
              <HiOutlineChatBubbleLeftRight />
            </div>
            <div>
              <h3 style={sectionTitle}>Send Request</h3>
              <p style={sectionSubtitle}>
                Ask admin to update targets, add a task, or add a new practice with tasks.
              </p>
            </div>
          </div>

          <label style={label}>Request Type</label>
          <select
            value={requestType}
            onChange={(e) => {
              setRequestType(e.target.value);
              setPractice("");
              setTask("");
              setTargetValue("");
              setNewPracticeName("");
              setTasksText("");
            }}
            style={input}
          >
            <option value="target_update">Target Update</option>
            <option value="task_addition">Add Task To Practice</option>
            <option value="practice_with_tasks">Add Practice With Tasks</option>
          </select>

          {(requestType === "target_update" || requestType === "task_addition") && (
            <>
              <label style={label}>Practice</label>
              <select
                value={practice}
                onChange={(e) => {
                  setPractice(e.target.value);
                  setTask("");
                }}
                style={input}
                disabled={loadingMasters}
              >
                <option value="">
                  {loadingMasters ? "Loading practices..." : "Select Practice"}
                </option>
                {practices.map((item) => (
                  <option key={item.id} value={item.name}>
                    {item.name}
                  </option>
                ))}
              </select>

              <label style={label}>
                {requestType === "target_update" ? "Task" : "Task To Add"}
              </label>
              {requestType === "target_update" ? (
                <select
                  value={task}
                  onChange={(e) => setTask(e.target.value)}
                  style={input}
                  disabled={!practice || loadingMasters}
                >
                  <option value="">
                    {!practice
                      ? "Select Practice First"
                      : loadingMasters
                      ? "Loading tasks..."
                      : "Select Task"}
                  </option>
                  {filteredTasks.map((item) => (
                    <option key={item.id} value={item.name}>
                      {item.name}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  value={task}
                  onChange={(e) => setTask(e.target.value)}
                  style={input}
                  placeholder="Enter new task name"
                />
              )}
            </>
          )}

          {requestType === "target_update" && (
            <>
              <label style={label}>Requested Target</label>
              <input
                value={targetValue}
                onChange={(e) => setTargetValue(e.target.value)}
                style={input}
                placeholder="Enter new target"
              />
            </>
          )}

          {requestType === "practice_with_tasks" && (
            <>
              <label style={label}>New Practice Name</label>
              <input
                value={newPracticeName}
                onChange={(e) => setNewPracticeName(e.target.value)}
                style={input}
                placeholder="Enter new practice name"
              />

              <label style={label}>Tasks</label>
              <textarea
                value={tasksText}
                onChange={(e) => setTasksText(e.target.value)}
                style={textarea}
                placeholder={"Enter tasks, for example:\nTask 1\nTask 2\nTask 3"}
              />
            </>
          )}

          <label style={label}>Message</label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            style={textarea}
            placeholder="Explain the request clearly..."
          />

          <button
            onClick={submitRequest}
            style={{
              ...submitBtn,
              opacity: submitting ? 0.75 : 1,
              cursor: submitting ? "not-allowed" : "pointer"
            }}
            disabled={submitting}
            type="button"
          >
            <HiOutlinePaperAirplane style={{ fontSize: 16 }} />
            {submitting ? "Sending..." : "Send Request"}
          </button>

          {submitMessage && (
            <div
              style={{
                ...messageBox,
                background: submitMessage.includes("✅") ? "#ECFDF5" : "#FEF2F2",
                color: submitMessage.includes("✅") ? theme.success : theme.danger,
                borderColor: submitMessage.includes("✅") ? "#BBF7D0" : "#FECACA"
              }}
            >
              {submitMessage.includes("✅") ? (
                <HiOutlineCheckCircle style={{ fontSize: 18, flexShrink: 0 }} />
              ) : (
                <HiOutlineExclamationTriangle style={{ fontSize: 18, flexShrink: 0 }} />
              )}
              <span>{submitMessage}</span>
            </div>
          )}
        </div>

        <div style={historyCard}>
          <div style={historyHeader}>
            <h3 style={sectionTitle}>Request History</h3>

            <div style={searchWrap}>
              <HiOutlineMagnifyingGlass style={searchIcon} />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search requests..."
                style={searchInput}
              />
            </div>
          </div>

          {loadingHistory ? (
            <div style={stateCard}>
              <HiOutlineClock style={{ fontSize: 20, color: theme.warning }} />
              <span>Loading requests...</span>
            </div>
          ) : filteredHistory.length === 0 ? (
            <div style={stateCard}>
              <HiOutlineChatBubbleLeftRight style={{ fontSize: 20, color: theme.primary }} />
              <span>No manager requests found</span>
            </div>
          ) : (
            <div style={historyList}>
              {filteredHistory.map((row) => {
                const statusUI = getStatusUI(row.status);

                return (
                  <div key={row.id} style={historyRow}>
                    <div style={historyTop}>
                      <div>
                        <div style={historyType}>
                          {requestTypeLabel(row.request_type)}
                        </div>
                        <div style={historyMeta}>
                          {row.practice || "-"} • {formatDisplayDate(row.created_at)}
                        </div>
                      </div>

                      <span
                        style={{
                          ...statusPill,
                          color: statusUI.color,
                          background: statusUI.bg
                        }}
                      >
                        {statusUI.text}
                      </span>
                    </div>

                    {row.task ? (
                      <div style={historyDetail}>
                        {row.request_type === "practice_with_tasks" ? "Tasks" : "Task"}: {row.task}
                      </div>
                    ) : null}

                    {row.requested_target ? (
                      <div style={historyDetail}>
                        Requested Target: {row.requested_target}
                      </div>
                    ) : null}

                    <div style={historyDetail}>
                      Message: {row.message || "-"}
                    </div>

                    <div style={replyBox}>
                      <strong>Admin Reply:</strong>{" "}
                      {row.admin_reply || "No reply yet"}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const page = {
  color: theme.text,
  fontFamily: "sans-serif"
};

const hero = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "16px",
  marginBottom: "24px",
  flexWrap: "wrap"
};

const title = {
  margin: 0,
  fontSize: "28px",
  fontWeight: 700
};

const subtitle = {
  margin: "8px 0 0 0",
  color: theme.textMuted,
  fontSize: "14px"
};

const refreshBtn = {
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  border: "none",
  background: "linear-gradient(135deg, #0F766E, #14B8A6)",
  color: "#FFFFFF",
  padding: "12px 16px",
  borderRadius: "12px",
  cursor: "pointer",
  fontWeight: 700
};

const layout = {
  display: "grid",
  gridTemplateColumns: "420px 1fr",
  gap: "20px"
};

const formCard = {
  background: "#FFFFFF",
  borderRadius: "20px",
  padding: "22px",
  border: `1px solid ${theme.border}`,
  boxShadow: "0 10px 24px rgba(15,23,42,0.05)"
};

const historyCard = {
  background: "#FFFFFF",
  borderRadius: "20px",
  padding: "22px",
  border: `1px solid ${theme.border}`,
  boxShadow: "0 10px 24px rgba(15,23,42,0.05)"
};

const sectionTitleRow = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
  marginBottom: "18px"
};

const iconWrap = {
  width: "42px",
  height: "42px",
  borderRadius: "14px",
  background: theme.surfaceSoft,
  color: theme.primary,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "20px"
};

const sectionTitle = {
  margin: 0,
  fontSize: "18px",
  fontWeight: 700
};

const sectionSubtitle = {
  margin: "4px 0 0 0",
  color: theme.textMuted,
  fontSize: "13px"
};

const label = {
  display: "block",
  marginBottom: "6px",
  marginTop: "12px",
  fontWeight: 600,
  fontSize: "13px"
};

const input = {
  width: "100%",
  height: "44px",
  borderRadius: "12px",
  border: "1px solid #CBD5E1",
  padding: "0 12px",
  fontSize: "14px",
  boxSizing: "border-box"
};

const textarea = {
  width: "100%",
  minHeight: "110px",
  borderRadius: "12px",
  border: "1px solid #CBD5E1",
  padding: "12px",
  fontSize: "14px",
  boxSizing: "border-box",
  resize: "vertical"
};

const submitBtn = {
  marginTop: "16px",
  width: "100%",
  height: "46px",
  borderRadius: "12px",
  border: "none",
  background: "linear-gradient(135deg, #0F766E, #14B8A6)",
  color: "#FFFFFF",
  fontWeight: 700,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px"
};

const messageBox = {
  marginTop: "14px",
  padding: "12px",
  borderRadius: "12px",
  border: "1px solid",
  display: "flex",
  alignItems: "center",
  gap: "10px",
  fontSize: "13px",
  fontWeight: 600
};

const historyHeader = {
  display: "flex",
  justifyContent: "space-between",
  gap: "16px",
  alignItems: "center",
  marginBottom: "16px",
  flexWrap: "wrap"
};

const searchWrap = {
  position: "relative",
  width: "280px",
  maxWidth: "100%"
};

const searchIcon = {
  position: "absolute",
  left: "12px",
  top: "50%",
  transform: "translateY(-50%)",
  color: theme.textMuted,
  fontSize: "18px"
};

const searchInput = {
  width: "100%",
  height: "42px",
  borderRadius: "12px",
  border: "1px solid #CBD5E1",
  padding: "0 12px 0 38px",
  fontSize: "14px",
  boxSizing: "border-box"
};

const stateCard = {
  minHeight: "120px",
  border: "1px dashed #CBD5E1",
  borderRadius: "16px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "10px",
  color: theme.textMuted
};

const historyList = {
  display: "flex",
  flexDirection: "column",
  gap: "14px"
};

const historyRow = {
  border: "1px solid #E2E8F0",
  borderRadius: "16px",
  padding: "16px"
};

const historyTop = {
  display: "flex",
  justifyContent: "space-between",
  gap: "14px",
  alignItems: "flex-start",
  marginBottom: "10px"
};

const historyType = {
  fontSize: "15px",
  fontWeight: 700,
  color: theme.text
};

const historyMeta = {
  marginTop: "4px",
  fontSize: "12px",
  color: theme.textMuted
};

const statusPill = {
  padding: "6px 12px",
  borderRadius: "999px",
  fontSize: "12px",
  fontWeight: 700,
  whiteSpace: "nowrap"
};

const historyDetail = {
  fontSize: "13px",
  color: theme.text,
  marginBottom: "8px",
  whiteSpace: "pre-wrap"
};

const replyBox = {
  background: "#F8FAFC",
  borderRadius: "12px",
  padding: "10px 12px",
  fontSize: "13px",
  color: theme.text
};

export default ManagerRequests;